import type { DataType, DataRecord } from '../types.js';
import type { parseOptions, InferArrayOptions } from './types.js';

export class InferError extends Error {
  path: string;
  type: string;
  value: unknown;

  constructor(path: string, type: string, value: unknown) {
    super(
      [
        '[Infer Error]',
        `[path: '${path}']`,
        `[expected type: ${type}]`,
        `[value: ${value}]`,
      ].join(' ')
    );

    this.path = path;
    this.type = type;
    this.value = value;
  }

  toString() {
    return [
      '[Infer Error]',
      `[path: '${this.path}']`,
      `[type: ${this.type}]`,
      `[value: ${this.value}]`,
    ].join(' ');
  }
}

/**
 * Inferer: safely infering data for managing and transforming data
 */
export class Inferer {
  _data: DataType;

  constructor(data: DataType) {
    this._data = data;
  }

  /**
   * create error object
   *
   * @private
   * @param {...string[]} tags
   * @return {Error}
   * @memberof Inferer
   */
  private _createError(path: string, type: string, value: unknown) {
    return new InferError(path, type, value);
  }

  /**
   * fallback handler for each infer type function
   *
   * @private
   * @template T
   * @param {string} path
   * @param {string} type
   * @param {DataType} value
   * @param {parseOptions<T>} options
   * @return {T}
   * @memberof Inferer
   */
  private _fallbackHandler<T>(
    path: string,
    type: string,
    value: DataType,
    options: parseOptions<T>
  ) {
    if (options.parseHandler) return options.parseHandler(value);
    if (options.default !== undefined) return options.default;
    throw this._createError(path, type, value);
  }

  /* ----------------------------- Public Utility ----------------------------- */

  /**
   * Get raw value based on object path
   */
  get(path = '') {
    if (path === '.' || path === '') return this._data;
    if (
      this._data === null ||
      this._data === undefined ||
      typeof this._data !== 'object'
    )
      return undefined;

    // based on you-dont-need-lodash repository
    // @see https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore?tab=readme-ov-file#_get
    const travel = (rgexp: RegExp) =>
      path
        .split(rgexp)
        .filter(Boolean)
        .reduce((val, key) => {
          if (val === null || val === undefined || typeof val !== 'object')
            return undefined;
          if (Object.hasOwn(val, key)) {
            return (val as DataRecord)[key];
          } else {
            return undefined;
          }
        }, this._data);

    return travel(/[,[\]]+?/) ?? travel(/[,[\].]+?/);
  }

  /**
   * check given path if value exist or not.
   *
   * @param {string} path
   * @param {(data: DataType) => boolean} [validator=(v) => v !== undefined] default to `(v) => v !== undefined`
   * @return {boolean}
   */
  has(
    path: string,
    validator: (data: DataType) => boolean = (v) => v !== undefined
  ) {
    const value = this.get(path);
    return validator(value);
  }

  /**
   * Infer object path in runtime either value is string or throw an error
   *
   * @param {string} [path='']
   * @param {parseOptions<string>} [options={}]
   * @return {string}
   * @memberof Inferer
   */
  string(path = '', options: parseOptions<string> = {}) {
    const value = this.get(path);
    if (typeof value === 'string') return value;
    return this._fallbackHandler(path, 'string', value, options);
  }

  /**
   * alias for string inferer
   */
  str(path = '', options: parseOptions<string> = {}) {
    return this.string(path, options);
  }

  /**
   * Infer object path in runtime either value is number (int|float) or throw an error
   *
   * @param {string} [path='']
   * @param {(parseOptions<number> & { float?: boolean })} [options={}]
   * @return {number}
   * @memberof Inferer
   */
  number(path = '', options: parseOptions<number> & { float?: boolean } = {}) {
    const value = this.get(path);
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      if (options.float === true) {
        const parsed = parseFloat(value);
        if (!isNaN(parsed)) return parsed;
      } else {
        const parsed = parseInt(value);
        if (!isNaN(parsed)) return parsed;
      }
    }
    return this._fallbackHandler(path, 'number', value, options);
  }

  /**
   * alias for number inferer
   */
  num(path = '', options: parseOptions<number> & { float?: boolean } = {}) {
    return this.number(path, options);
  }

  /**
   * Infer object path in runtime either value is boolean or throw an error
   *
   * @param {string} [path='']
   * @param {parseOptions<boolean>} [options={}]
   * @return {boolean}
   * @memberof Inferer
   */
  boolean(path = '', options: parseOptions<boolean> & { force?: boolean } = {}) {
    const value = this.get(path);
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return !!value;
    if (typeof value === 'string') {
      if (value.toLowerCase() === 'true') return true
      if (value.toLowerCase() === 'false') return false
    }

    if (options.force === true) {
      if (typeof value === 'undefined') return false;
      if (value === null) return false;
    }

    return this._fallbackHandler(path, 'boolean', value, options);
  }

  /**
   * alias for boolean inferer
   */
  bool(path = '', options: parseOptions<boolean> = {}) {
    return this.boolean(path, options);
  }

  /**
   * Infer object path in runtime accepting value of string or number then return as a string.
   *
   * Return error when neither string or number found.
   * Usefull for id field that you are unsure is it number or string value.
   *
   * @param {string} [path='']
   * @param {parseOptions<string>} [options={}]
   * @return {string}
   * @memberof Inferer
   */
  stringNumber(path = '', options: parseOptions<string> = {}) {
    const value = this.get(path);
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value + '';
    return this._fallbackHandler(path, 'stringNumber', value, options);
  }

  /** alias for stringOrNumber */
  snum(path = '', options: parseOptions<string> = {}) {
    return this.stringNumber(path, options);
  }

  /**
   * Infer object path in runtime is array then return as InfererArray object.
   *
   * @param {boolean} [defaultEmpty=false] when true return will fallback to empty array. On false will throw error. default: false.
   */
  array(path = '', options: parseOptions<InfererArray> & { defaultEmpty?: boolean } & InferArrayOptions = {}) {
    const value = this.get(path);
    if (value instanceof Array) return new InfererArray(value, { ...options, path, value });
    if (options.default instanceof Array) return new InfererArray(options.default, { ...options, path, value });
    if (options.defaultEmpty === true) return new InfererArray([], { ...options, path, value });
    return this._fallbackHandler<InfererArray>(path, 'array', value, {});
  }

  /**
   * Infer object path when path has value it will parse or transform with given function or throw an error when value is null or undefined.
   */
  parse<T = DataType>(
    path: string,
    fn: (data: unknown) => T,
    options: parseOptions<T> = {}
  ) {
    const value = this.get(path);
    if (value !== null && value !== undefined) return fn(value);
    return this._fallbackHandler(path, 'parse', value, options);
  }

  /**
   * Infer object path and fallback to given preset value and type if type not match
   */
  maybe<T>(path: string, preset: T) {
    const wrap = <Fn extends () => unknown>(fn: Fn) => {
      try {
        return fn() as ReturnType<Fn>;
      } catch (error) {
        if (error instanceof InferError) {
          console.warn(error.toString());
        } else console.warn(error);
        return preset;
      }
    };

    const _string = (options: parseOptions<string> = {}) => {
      return wrap(() => this.string(path, options));
    };

    const _number = (options: parseOptions<number> = {}) => {
      return wrap(() => this.number(path, options));
    };

    const _boolean = (options: parseOptions<boolean> & { force?: boolean } = {}) => {
      return wrap(() => this.boolean(path, options));
    };

    const _snum = (options: parseOptions<string> = {}) => {
      return wrap(() => this.snum(path, options));
    };

    const _parse = <V>(
      fn: (data: unknown) => V,
      options: parseOptions<V> = {}
    ) => {
      return wrap(() => this.parse(path, fn, options));
    };

    const _array = (options: parseOptions<InfererArray> & { defaultEmpty?: boolean } = {}) => {
      return wrap(() => this.array(path, options));
    };

    return {
      /** check if given path is a string or return preset value */
      string: _string,

      /** check if given path is a string or return preset value */
      str: _string,

      /** check if given path is a number or return preset value */
      number: _number,

      /** check if given path is a number or return preset value */
      num: _number,

      /** check if given path is a boolean or return preset value */
      boolean: _boolean,

      /** check if given path is a boolean or return preset value */
      bool: _boolean,

      /** check if given path is a string, number or return preset value */
      stringNumber: _snum,

      /** check if given path is a string, number or return preset value */
      snum: _snum,

      /** check if given path has value then parse using given function or return preset value */
      parse: _parse,

      /** check if given path is array then return InfererArray or return preset value */
      array: _array,
    };
  }

  /**
   * alias or shortcut for maybe with preset value null
   */
  nullable(path = '') {
    return this.maybe(path, null);
  }
}

/**
 * Extended array object with typesafe infering and transform capability
 *
 * @export
 * @class InfererArray
 * @extends {Array}
 */
export class InfererArray extends Array {

  private path: string
  private value: unknown
  private skipError = false;

	constructor(collection: DataType[], options: InferArrayOptions & { path: string, value: unknown }) {
		super();
		this.push(...collection);
    this.path = options.path;
    this.value = options.value;
    this.skipError = options.skipError ?? false;
	}

	/**
	 * loop each item and parse or transform item using given function.
	 *
	 * @param {(row: Inferer, index: number, arr: InfererArray) => T} fn
	 * @return {T[]} new transformed array
	 */
	inferEach<T = DataType>(fn: (row: Inferer, index: number, arr: InfererArray) => T) {
		const collection: T[] = [];
		for (let i = 0; i < this.length; i++) {
			const row = this[i];
      try {
        const inferedRow = fn(new Inferer(row), i, this); 
        collection.push(inferedRow);
      } catch(err) {
        if (!this.skipError) {
          if (err instanceof Error) throw err
          else if (typeof err === 'string') throw new Error(err);
          else throw new Error(`Fail to infer array at row ${i} with value of ${row}`)
        } else {
          console.warn(new InferError(this.path, 'array', this.value).toString());
        }
      }
		}

		return collection;
	}

	/**
	 * extended Array.at that return inferer object.
	 *
	 * @param {number} index The zero-based index of the desired code unit. A negative index will count back from the last item.
	 */
	inferAt(index: number) {
		return new Inferer(this.at(index));
	}
}